import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from "react";
import "./CircularTimer.css";

interface CircularTimerProps {
  totalSeconds: number;
  remainingSeconds: number;
  onTimeUp?: () => void;
  size?: number;
  strokeWidth?: number;
  primaryColor?: string;
  secondaryColor?: string;
  textColor?: string;
  autoCalculateTime?: boolean;
  timerFontSize?: number;
}

export interface CircularTimerRef {
  reset: (customTime?: number) => void;
  pause: () => void;
  resume: () => void;
  getCurrentTime: () => number;
}

const CircularTimerAuto = forwardRef<CircularTimerRef, CircularTimerProps>(
  (
    {
      totalSeconds = 0,
      remainingSeconds = 0,
      onTimeUp,
      size = 120,
      strokeWidth = 8,
      primaryColor = "#ff6b6b",
      secondaryColor = "#f0f0f0",
      textColor = "#333",
      autoCalculateTime = false,
      timerFontSize = 24,
    },
    ref
  ) => {
    const [totalTime, setTotalTime] = useState(totalSeconds);
    const [timeLeft, setTimeLeft] = useState(remainingSeconds);
    const [isPaused, setIsPaused] = useState(false);
    const [isRunning, setIsRunning] = useState(true);

    // Update totalTime when totalSeconds prop changes
    useEffect(() => {
      setTotalTime(totalSeconds);
    }, [totalSeconds]);

    // Update timeLeft when remainingSeconds prop changes
    useEffect(() => {
      setTimeLeft(remainingSeconds);
    }, [remainingSeconds]);

    // auto change seconds after every 1 second if autoCalculateTime is true stop when timeLeft is 0
    useEffect(() => {
      if (autoCalculateTime && timeLeft > 0) {
        const interval = setInterval(() => {
          setTimeLeft(timeLeft - 1);
        }, 1000);
        return () => clearInterval(interval);
      }
    }, [autoCalculateTime, timeLeft]);

    // Calculate circle properties
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const progress = timeLeft / totalTime;
    const strokeDasharray = circumference;
    // Calculate strokeDashoffset for anticlockwise unfilling from top
    const strokeDashoffset = circumference * (1 - progress);

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
      reset: (customTime?: number) => {
        const timeToSet = customTime !== undefined ? customTime : totalTime;
        setTimeLeft(timeToSet);
        setIsRunning(true);
        setIsPaused(false);
      },
      pause: () => {
        setIsPaused(true);
        setIsRunning(false);
      },
      resume: () => {
        setIsPaused(false);
        setIsRunning(true);
      },
      getCurrentTime: () => timeLeft,
    }));

    // Format time display
    const formatTime = (seconds: number) => {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
        .toString()
        .padStart(2, "0")}`;
    };

    return (
      <div className="circular-timer" style={{ width: size, height: size }}>
        <svg
          width={size}
          height={size}
          className="circular-timer-svg"
          style={{ position: "absolute", zIndex: 1 }}
        >
          {/* Background circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={secondaryColor}
            strokeWidth={strokeWidth}
            fill="none"
          />

          {/* Progress circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={primaryColor}
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className="progress-circle"
            style={{ filter: "drop-shadow(0 0 2px rgba(0,0,0,0.3))" }}
          />
        </svg>

        {/* Time display */}
        <div
          className="timer-text"
          style={{
            color: textColor,
            position: "relative",
            zIndex: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            width: "100%",
            fontSize: timerFontSize,
          }}
        >
          {formatTime(timeLeft)}
        </div>

        {/* Status indicator */}
        {isPaused && (
          <div
            className="pause-indicator"
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 3,
              fontSize: "24px",
            }}
          >
            ⏸
          </div>
        )}
      </div>
    );
  }
);

CircularTimerAuto.displayName = "CircularTimerAuto";

export default CircularTimerAuto;
